<template>
  <v-data-table
    :headers="headers"
    :items="localItems"
    :pagination.sync="pagination"
    :rows-per-page-items="rowsPerPageItems"
    :hide-actions="hideActions"
    :loading="loading"
    :search="search"
    class="elevation-0"
    item-key="id"
  >
    <template v-slot:progress>
      <v-progress-linear
        color="success"
        height="3"
        indeterminate
      />
    </template>

    <template v-slot:items="{ item }">
      <tr>
        <td :rowspan="calcRowspan(item.analysis.results)">
          <router-link :to="linkTo(item)">
            {{ item.trial.name }}
          </router-link>
        </td>
        <td :rowspan="calcRowspan(item.analysis.results)" class="no-wrap">
          {{ item.workdate }}
        </td>
        <td :rowspan="calcRowspan(item.analysis.results)">
          {{ item.product_code }}
        </td>
        <td :rowspan="calcRowspan(item.analysis.results)">
          {{ item.laboratory.name }}
        </td>
        <td :rowspan="calcRowspan(item.analysis.results)">
          {{ item.analysis.name }}
        </td>
      </tr>
      <tr v-for="(value, key, index) in item.analysis.results" :key="`${item.id}_${key}`">
        <td>
          <v-text-field
            class="mb-1"
            v-model="item.analysis.results[key]"
            @input.native="onResultInput(item)"
            :label="item.analysis.measure"
            :type="item.analysis.type"
            :height="20"
            hide-details
          ></v-text-field>
        </td>
        <td v-if="!index"
            :rowspan="calcRowspan(item.analysis.results)"
            class="text-xs-center"
        >
          <dashboard-comments-badge
            :commentState="commentState(item)"
          />
        </td>
        <td v-if="!index"
            :rowspan="calcRowspan(item.analysis.results)"
            class="text-xs-center"
        >
          <v-autocomplete
            v-model="item.status"
            @change="setChanged(item)"
            :items="ITEM_STATUS"
            name="status"
            label="Status"
            item-text="name"
            item-value="name"
            :color="statusColor(item.status)"
            class="pa-0 ma-0"
            small
            single-line
            hide-details
            required
          >
            <template v-slot:item="{ item }">
              <v-list-tile-title
                v-text="item.name"
                :class="`${item.color}--text`"
              ></v-list-tile-title>
            </template>
          </v-autocomplete>
        </td>
      </tr>
    </template>

    <template v-slot:actions-append>
      <template v-if="isChanged">
        <v-btn
          @click="resetAnalysis"
          :disabled="loading"
          flat
          color="warning"
        >
          Reset
        </v-btn>
        <v-btn
          :loading="loading"
          :disabled="loading"
          @click="saveAnalysis"
          color="success"
        >
          Save
        </v-btn>
      </template>
    </template>
  </v-data-table>
</template>

<script>
import dashboardCommentsBadge from '@/components/comments/dashboardCommentsBadge';

export default {
  name: 'dataTable',
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    headers: {
      type: Array,
      default() {
        return [];
      },
    },
    loading: {
      type: Boolean,
      default() {
        return false;
      },
    },
    search: {
      type: String,
      default() {
        return '';
      },
    },
  },
  data() {
    return {
      rowsPerPageItems: [
        { 'text': '$vuetify.dataIterator.rowsPerPageAll', 'value': -1 },
      ],
      hideActions: false,
      isChanged: false,
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 3,
        sortBy: 'workdate',
        totalItems: 0,
      },
      localItems: JSON.parse(JSON.stringify(this.items)),
    };
  },
  mounted() {
    this.updatePaginationMeta();
  },
  watch: {
    items: {
      immediate: true,
      handler(items) {
        this.localItems = JSON.parse(JSON.stringify(items));
        this.updatePaginationMeta();
        this.$nextTick(() => {
          this.isChanged = false;
        });
      },
    },
    localItems: {
      deep: true,
      handler() {
        this.isChanged = true;
      },
    },
  },
  methods: {
    commentState(item) {
      return {
        module: 'ANALYSIS',
        id: item.trial_id,
        type: 'trials',
        uniqueId: item.unique_id,
      };
    },
    onResultInput(item) {
      this.setChanged(item);
      if (item.analysis.status !== 'DONE' || item.analysis.status !== 'CANCELED') {
        item.status = 'DONE';
        item.analysis.status = 'DONE';
      }
    },
    setChanged(item) {
      item.changed = true;
    },
    updatePaginationMeta() {
      const count = this.items.length;
      const totalPages = Math.ceil(count / this.pagination.rowsPerPage);
      this.pagination.totalItems = count;
      this.hideActions = !count;
      if (count > this.pagination.rowsPerPage) {
        const pages = [];
        for (let i = 0; i < totalPages; i++) {
          if (i !== 0 && i % this.pagination.rowsPerPage === 0) {
            pages.push(i);
            continue;
          }
          pages.push(this.pagination.rowsPerPage);
        }
        this.rowsPerPageItems = [
          ...pages,
          ...this.rowsPerPageItems,
        ];
      }
    },
    calcRowspan(obj) {
      return Object.keys(obj).length + 1;
    },
    resetAnalysis() {
      this.localItems = JSON.parse(JSON.stringify(this.items));
      this.$nextTick(() => {
        this.isChanged = false;
      });
    },
    saveAnalysis() {
      this.$emit('saveAnalysis', this.localItems.filter(item => item.changed));
    },
    linkTo(item) {
      return {
        name: this.allRoutes.trial.children.trialDetail.children.trialAnalysis.name,
        params: {
          id: item.trial_id,
          from: this.$route.name,
        },
      };
    },
  },
  components: {
    dashboardCommentsBadge,
  },
};
</script>

<style scoped>
table tr {
  border: 0 !important;
  background: none !important;
}

table td,
table th {
  border-right: 1px solid rgb(228, 228, 228);
  border-top: 1px solid rgb(228, 228, 228) !important;
  padding: 5px!important;
}

table td:first-child,
table th:first-child {
  border-left: 1px solid rgb(228, 228, 228);
}

table thead tr:first-child th {
  border-top: 1px solid rgb(228, 228, 228);
}

table tr + .v-datatable__expand-row {
  border: 1px solid rgb(228, 228, 228) !important;
  border-bottom: 0 !important;
}

.v-datatable__progress {
  border: 0 !important;
}
</style>
